import React, { useEffect, useState } from "react";
import { Lock, LogOut, Users, Check, X, Mail, Trash2, Music, Image as ImageIcon, Download } from "lucide-react";
import { toast } from "sonner";
import {
  adminLogin, getToken, adminLogout, getRSVPs,
  adminGetGuests, adminAddGuest, adminDeleteGuest,
  adminGetPhotos, adminApprovePhoto, adminDeletePhoto,
} from "../lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";

export default function Admin() {
  const [authed, setAuthed] = useState(!!getToken());
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");

  const login = async (e) => {
    e.preventDefault();
    try {
      await adminLogin(username, password);
      setAuthed(true);
      toast.success("Welcome back!");
    } catch {
      toast.error("Invalid username or password.");
    }
  };
  const logout = () => { adminLogout(); setAuthed(false); };

  if (!authed) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F4EFE6] px-6">
        <form onSubmit={login} className="w-full max-w-sm bg-[#F9F5EE] border border-[#211E1A]/10 p-10 rounded-sm shadow-xl">
          <div className="w-12 h-12 rounded-full bg-[#9E2B25] text-[#F4EFE6] flex items-center justify-center mx-auto">
            <Lock size={20} />
          </div>
          <h1 className="mt-6 text-center font-serif-display text-3xl text-[#211E1A]">Admin Login</h1>
          <p className="mt-2 text-center text-sm text-[#6b6157]">Sign in to manage your wedding</p>
          <input value={username} onChange={(e) => setUsername(e.target.value)} placeholder="Username"
            className="mt-8 w-full bg-transparent border-b border-[#211E1A]/25 py-2.5 focus:outline-none focus:border-[#9E2B25]" />
          <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Password"
            className="mt-6 w-full bg-transparent border-b border-[#211E1A]/25 py-2.5 focus:outline-none focus:border-[#9E2B25]" />
          <button className="mt-8 w-full bg-[#211E1A] text-[#F4EFE6] text-[12px] uppercase tracking-widest-2 py-3.5 rounded-sm hover:bg-[#9E2B25] transition-colors">
            Sign In
          </button>
          <p className="mt-4 text-center text-[11px] text-[#9c9488]">Demo: admin / lovewins</p>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F4EFE6]">
      <header className="border-b border-[#211E1A]/12 px-6 md:px-10 py-5 flex items-center justify-between">
        <h1 className="font-serif-display text-2xl text-[#211E1A]">Wedding Admin</h1>
        <button onClick={logout} className="inline-flex items-center gap-2 text-[12px] uppercase tracking-widest-2 text-[#211E1A] hover:text-[#9E2B25]">
          <LogOut size={15} /> Logout
        </button>
      </header>

      <div className="max-w-[1100px] mx-auto px-6 py-10">
        <Tabs defaultValue="rsvps">
          <TabsList className="bg-[#ECE3D4] rounded-sm">
            <TabsTrigger value="rsvps">RSVPs</TabsTrigger>
            <TabsTrigger value="guests">Guest List</TabsTrigger>
            <TabsTrigger value="photos">Photos</TabsTrigger>
          </TabsList>
          <TabsContent value="rsvps"><RsvpTab /></TabsContent>
          <TabsContent value="guests"><GuestsTab /></TabsContent>
          <TabsContent value="photos"><PhotosTab /></TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

function Stat({ label, value, icon }) {
  return (
    <div className="bg-[#F9F5EE] border border-[#211E1A]/10 rounded-sm p-6">
      <div className="flex items-center gap-2 text-[#9E2B25]">{icon}
        <span className="text-[11px] uppercase tracking-widest-2 text-[#6b6157]">{label}</span>
      </div>
      <p className="mt-3 font-serif-display text-4xl text-[#211E1A]">{value}</p>
    </div>
  );
}

function RsvpTab() {
  const [rsvps, setRsvps] = useState([]);
  useEffect(() => { getRSVPs().then(setRsvps).catch(() => setRsvps([])); }, []);
  const attending = rsvps.filter((r) => r.attending === "yes");
  const totalGuests = attending.reduce((s, r) => s + 1 + Number(r.guests || 0), 0);

  const exportCSV = () => {
    if (rsvps.length === 0) { toast.error("No RSVPs to export yet."); return; }
    const headers = ["Name", "Email", "Attending", "Meal", "Additional Guests", "Song Request", "Artist", "Notes", "Submitted"];
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const rows = rsvps.map((r) => [
      r.full_name, r.email, r.attending, r.meal, r.guests, r.song_request, r.song_artist, r.notes,
      r.created_at ? new Date(r.created_at).toLocaleString() : "",
    ].map(esc).join(","));
    const csv = [headers.map(esc).join(","), ...rows].join("\n");
    const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `wedding-rsvps-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("RSVP spreadsheet downloaded.");
  };

  return (
    <div className="mt-8">
      <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
        <div className="grid grid-cols-3 gap-5 flex-1 min-w-[280px]">
          <Stat label="Total Responses" value={rsvps.length} icon={<Mail size={18} />} />
          <Stat label="Attending" value={attending.length} icon={<Check size={18} />} />
          <Stat label="Expected Guests" value={totalGuests} icon={<Users size={18} />} />
        </div>
        <button
          onClick={exportCSV}
          className="inline-flex items-center gap-2 bg-[#211E1A] text-[#F4EFE6] text-[12px] uppercase tracking-widest-2 px-6 py-3 rounded-sm hover:bg-[#9E2B25] transition-colors self-start"
        >
          <Download size={15} /> Export CSV
        </button>
      </div>
      <div className="bg-[#F9F5EE] border border-[#211E1A]/10 rounded-sm overflow-x-auto">
        <table className="w-full text-left text-sm">
          <thead className="bg-[#ECE3D4] text-[#211E1A]">
            <tr>
              {["Name", "Email", "Attending", "Meal", "Guests", "Song Request", "Notes"].map((h) => (
                <th key={h} className="px-4 py-3 text-[11px] uppercase tracking-widest-2 font-semibold whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rsvps.length === 0 ? (
              <tr><td colSpan={7} className="px-4 py-10 text-center text-[#6b6157]">No RSVPs yet.</td></tr>
            ) : rsvps.map((r) => (
              <tr key={r.id} className="border-t border-[#211E1A]/8 align-top">
                <td className="px-4 py-3 text-[#211E1A] font-medium whitespace-nowrap">{r.full_name}</td>
                <td className="px-4 py-3 text-[#4a443d]">{r.email}</td>
                <td className="px-4 py-3">
                  {r.attending === "yes"
                    ? <span className="inline-flex items-center gap-1 text-[#2e7d32]"><Check size={14}/> Yes</span>
                    : <span className="inline-flex items-center gap-1 text-[#9E2B25]"><X size={14}/> No</span>}
                </td>
                <td className="px-4 py-3 text-[#4a443d]">{r.meal || "—"}</td>
                <td className="px-4 py-3 text-[#4a443d]">{r.guests || "0"}</td>
                <td className="px-4 py-3 text-[#4a443d]">
                  {r.song_request ? (
                    <span className="inline-flex items-center gap-1"><Music size={13} className="text-[#9E2B25]"/> {r.song_request}{r.song_artist ? ` — ${r.song_artist}` : ""}</span>
                  ) : "—"}
                </td>
                <td className="px-4 py-3 text-[#4a443d] max-w-[220px]">{r.notes || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function GuestsTab() {
  const [guests, setGuests] = useState([]);
  const [name, setName] = useState("");
  const [plusOne, setPlusOne] = useState(false);
  const [plusOneName, setPlusOneName] = useState("");

  const load = () => adminGetGuests().then(setGuests).catch(() => setGuests([]));
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    if (!name.trim()) { toast.error("Enter a guest name."); return; }
    try {
      await adminAddGuest({ name: name.trim(), plus_one: plusOne, plus_one_name: plusOneName.trim() });
      toast.success("Guest added.");
      setName(""); setPlusOne(false); setPlusOneName("");
      load();
    } catch { toast.error("Could not add guest."); }
  };
  const remove = async (id) => {
    try { await adminDeleteGuest(id); load(); } catch { toast.error("Could not remove guest."); }
  };

  return (
    <div className="mt-8 grid grid-cols-1 lg:grid-cols-3 gap-8">
      <form onSubmit={add} className="bg-[#F9F5EE] border border-[#211E1A]/10 rounded-sm p-6 h-fit">
        <h3 className="font-serif-display text-2xl text-[#211E1A]">Add Invited Guest</h3>
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Guest full name"
          className="mt-5 w-full bg-transparent border-b border-[#211E1A]/25 py-2.5 focus:outline-none focus:border-[#9E2B25]" />
        <label className="mt-5 flex items-center gap-2 cursor-pointer">
          <span onClick={() => setPlusOne(!plusOne)}
            className={`w-4 h-4 border rounded-[3px] flex items-center justify-center ${plusOne ? "bg-[#9E2B25] border-[#9E2B25]" : "border-[#211E1A]/40"}`}>
            {plusOne && <Check size={12} className="text-[#F4EFE6]" />}
          </span>
          <span className="text-[#211E1A] text-sm">Plus-one allowed</span>
        </label>
        {plusOne && (
          <input value={plusOneName} onChange={(e) => setPlusOneName(e.target.value)} placeholder="Plus-one name (optional)"
            className="mt-4 w-full bg-transparent border-b border-[#211E1A]/25 py-2.5 focus:outline-none focus:border-[#9E2B25]" />
        )}
        <button className="mt-6 w-full bg-[#211E1A] text-[#F4EFE6] text-[12px] uppercase tracking-widest-2 py-3 rounded-sm hover:bg-[#9E2B25] transition-colors">
          Add Guest
        </button>
      </form>

      <div className="lg:col-span-2 bg-[#F9F5EE] border border-[#211E1A]/10 rounded-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-[#211E1A]/10 flex items-center justify-between">
          <h3 className="font-serif-display text-2xl text-[#211E1A]">Invited Guests</h3>
          <span className="text-sm text-[#6b6157]">{guests.length} total</span>
        </div>
        {guests.length === 0 ? (
          <p className="px-6 py-10 text-center text-[#6b6157]">No guests added yet. Add guests so they can find their invitation.</p>
        ) : (
          <ul className="divide-y divide-[#211E1A]/8">
            {guests.map((g) => (
              <li key={g.id} className="px-6 py-4 flex items-center justify-between">
                <div>
                  <p className="text-[#211E1A] font-medium">{g.name}</p>
                  <p className="text-sm text-[#6b6157]">
                    {g.plus_one ? `Plus-one${g.plus_one_name ? `: ${g.plus_one_name}` : " allowed"}` : "Individual invite"}
                  </p>
                </div>
                <button onClick={() => remove(g.id)} className="text-[#9E2B25] hover:text-[#872019]" aria-label="Remove">
                  <Trash2 size={17} />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function PhotosTab() {
  const [photos, setPhotos] = useState([]);
  const load = () => adminGetPhotos().then(setPhotos).catch(() => setPhotos([]));
  useEffect(() => { load(); }, []);

  const approve = async (id) => { try { await adminApprovePhoto(id); toast.success("Photo approved."); load(); } catch { toast.error("Failed."); } };
  const del = async (id) => { try { await adminDeletePhoto(id); load(); } catch { toast.error("Failed."); } };

  const pending = photos.filter((p) => !p.approved);
  const approved = photos.filter((p) => p.approved);

  return (
    <div className="mt-8">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mb-8">
        <Stat label="Pending Approval" value={pending.length} icon={<ImageIcon size={18} />} />
        <Stat label="Approved" value={approved.length} icon={<Check size={18} />} />
      </div>
      {photos.length === 0 ? (
        <p className="bg-[#F9F5EE] border border-[#211E1A]/10 rounded-sm px-6 py-10 text-center text-[#6b6157]">
          No guest photos uploaded yet.
        </p>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
          {photos.map((p) => (
            <div key={p.id} className="bg-[#F9F5EE] border border-[#211E1A]/10 rounded-sm overflow-hidden">
              <div className="aspect-square overflow-hidden">
                <img src={p.url} alt={p.uploader_name} className="w-full h-full object-cover" />
              </div>
              <div className="p-3">
                <p className="text-sm text-[#211E1A] truncate">{p.uploader_name}</p>
                {p.caption ? <p className="text-xs text-[#6b6157] italic mt-1 line-clamp-2">“{p.caption}”</p> : null}
                <div className="mt-2 flex items-center gap-2">
                  {!p.approved && (
                    <button onClick={() => approve(p.id)} className="flex-1 bg-[#211E1A] text-[#F4EFE6] text-[11px] uppercase tracking-widest-2 py-2 rounded-sm hover:bg-[#9E2B25]">
                      Approve
                    </button>
                  )}
                  {p.approved && <span className="flex-1 text-[11px] uppercase tracking-widest-2 text-[#2e7d32]">Approved</span>}
                  <button onClick={() => del(p.id)} className="text-[#9E2B25] hover:text-[#872019] px-2" aria-label="Delete">
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
